/**
 * R8 Eigentümerwechsel: Jede Zeile gehört dem Eigentümer, der die Immobilie im Steuermonat hält.
 * Liegt performAt im Monat, wird der Betrag tagesgenau geteilt (share = Tage / Monatstage) und
 * ein HINWEIS LANDLORD_CHANGE_SPLIT erzeugt. Fenster: `from` inklusiv, `to` exklusiv (= performAt
 * des Wechsels), tagesgenau. Noch nicht ausgeführte SCHEDULED-Wechsel (performAt < now) blocken.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
import {createIssue, mergeIssues} from '../issues';
import {propertyDeepLink, rescaleLine, withIssue} from '../line';
import {daysInMonth, formatDayDE, monthLabel, parseMonthKey} from '../month';
import {EMPTY_RULE_OUTPUT, OwnershipWindow, RuleContext, RuleOutput, VatIssue, VatLine} from '../types';

export interface OwnershipShareResult {
    share: number;
    ownedDays: number;
    totalDays: number;
    split: boolean;
}

/** Lokaler Tagesbeginn als Zeitstempel – Uhrzeiten in from/to spielen keine Rolle (tagesgenau). */
const startOfDay = (date: Date): number => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

/** Deckt das Fenster den Kalendertag ab? `from` inklusiv, `to` exklusiv; geprüft wird die Tagesmitte. */
const covers = (window: OwnershipWindow, dayNoon: Date): boolean =>
    (window.from === null || startOfDay(window.from) <= dayNoon.getTime())
    && (window.to === null || dayNoon.getTime() < startOfDay(window.to));

export const ownershipShareForMonth = (ctx: RuleContext, propertyID: string, monthKey: string): OwnershipShareResult => {
    const parts = parseMonthKey(monthKey);
    if (!parts) {
        throw new Error(`Ungültiger monthKey: ${monthKey}`);
    }
    const totalDays = daysInMonth(parts.year, parts.month);
    const windows = ctx.ownership.filter(w => w.propertyID === propertyID);
    if (windows.length === 0) {
        return {share: 1, ownedDays: totalDays, totalDays, split: false};
    }
    const mine = windows.filter(w => w.landlordID === ctx.landlordID);
    let ownedDays = 0;
    for (let day = 1; day <= totalDays; day++) {
        const noon = new Date(parts.year, parts.month - 1, day, 12);
        if (mine.some(w => covers(w, noon))) {
            ownedDays++;
        }
    }
    return {share: ownedDays / totalDays, ownedDays, totalDays, split: ownedDays > 0 && ownedDays < totalDays};
};

export const applyOwnershipToLines = (ctx: RuleContext, lines: VatLine[]): RuleOutput => {
    const out = EMPTY_RULE_OUTPUT();
    for (const line of lines) {
        const result = ownershipShareForMonth(ctx, line.source.propertyID, line.monthKey);
        if (result.ownedDays === 0) {
            continue; // gehört einem anderen Eigentümer
        }
        if (!result.split) {
            out.lines.push(line);
            continue;
        }
        const issue = createIssue('LANDLORD_CHANGE_SPLIT', `${line.source.propertyID}_${line.monthKey}`,
            `Eigentümerwechsel im ${monthLabel(line.monthKey)}: Die Beträge dieser Immobilie werden mit ${result.ownedDays} von ${result.totalDays} Tagen angesetzt. Bitte bestätigen oder die Aufteilung mit der Steuerberatung abstimmen.`,
            {deepLink: propertyDeepLink(line.source.propertyID)});
        const scaled = withIssue(rescaleLine(line, result.share), issue.id);
        out.lines.push(scaled);
        out.issues.push({...issue, lineIDs: [scaled.id]});
    }
    // ein Hinweis je Immobilie und Monat, mit allen betroffenen Zeilen
    out.issues = mergeIssues(out.issues);
    return out;
};

export const buildPendingChangeIssues = (ctx: RuleContext): VatIssue[] =>
    ctx.pendingChanges
        .filter(change => change.performAt.getTime() < ctx.now.getTime())
        .map(change => createIssue('LANDLORD_CHANGE_PENDING', change.propertyID,
            `Für diese Immobilie ist ein Eigentümerwechsel zum ${formatDayDE(change.performAt)} geplant, der noch nicht ausgeführt wurde. Bis dahin sind die Eigentümerfenster unvollständig.`,
            {deepLink: propertyDeepLink(change.propertyID)}));
